// src/pages/ParentRegister.tsx
//
// Same pattern as tutor: collect everything in state, submit on final step.
// No files for parents, but uses registerWithProfile for consistency.

import React, { useEffect, useState } from 'react';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { Helmet } from 'react-helmet-async';
import { Area, Button } from '../styles/_app.style';
import {
  Form,
  Tagline,
  IconButton,
  BackButton,
  FormActions,
  StepContent,
  StepperArea,
  FormArea,
  RegisterArea,
} from '../styles/login.style';
import { Link, useNavigate } from 'react-router-dom';
import useAuth, { registrationData, ProfilePatch } from '../hooks/auth.service';
import { titles } from '../constants/titles';
import { useToast } from '../contextManager/toast.context';
import { ChevronLeft } from 'lucide-react';

import PersonalDetailsStep from './steps/PersonalDetailsStep';
import ExpertiseStep from './steps/ExpertiseStep';
import { AvailabilityStep } from './steps/AvailabilityStep';
import { ProgressStepper } from '../components/register/ProgressStepper';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { getRandomPastelColor } from '../constants/colors';
import { useScale } from './HeightScaler';
import { AvailabilityTimezone } from '../components/register/InfoBlock';
import { BookLogo } from '../components/common/BookLogo';

export type ParentStep = 0 | 1 | 2;

const stepTaglines = [
  "Let's create your account",
  'What subjects are you looking for help with?',
  'When are you usually free for lessons?',
] as const;

const stepLabels = ['Your Details', 'Subjects Needed', 'Availability'];

const progressWidths = ['33.3%', '66.7%', '100%'];

const ParentRegister = () => {
  const { registerWithProfile } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { scale } = useScale();

  const [step, setStep] = useState<ParentStep>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── State ─────────────────────────────────────────────────────────
  const [account, setAccount] = useState<registrationData>({
    name: '',
    email: '',
    password: '',
    role: 'parent',
  });

  const [profile, setProfile] = useState<ProfilePatch>({
    profileColor: getRandomPastelColor(),
    availability: {
      monday: [], tuesday: [], wednesday: [], thursday: [],
      friday: [], saturday: [], sunday: [],
    },
  });

  const [detailsValid, setDetailsValid] = useState(false);
  const [expertiseValid, setExpertiseValid] = useState(false);
  const [availabilityValid, setAvailabilityValid] = useState(false);

  function savePatch(p: ProfilePatch) {
    setProfile((prev) => ({ ...prev, ...p }));
  }

  // ── Progress bar ──────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.style.setProperty('--progress-width', progressWidths[step]);
    document.body.classList.add('register-page-active');
    return () => {
      document.documentElement.style.removeProperty('--progress-width');
      document.body.classList.remove('register-page-active');
    };
  }, [step]);

  // ── Navigation ────────────────────────────────────────────────────
  const next = () => setStep((s) => (s < 2 ? ((s + 1) as ParentStep) : s));
  const prev = () => setStep((s) => (s > 0 ? ((s - 1) as ParentStep) : s));

  const canProceed = () => {
    if (step === 0) return detailsValid;
    return true;
  };

  // ── handleNext: no API calls until final step ─────────────────────
  const handleNext = async () => {
    if (step < 2) {
      next();
      return;
    }

    // ── Step 2 (Finish): single request ───────────────────────────
    setIsSubmitting(true);

    try {
      await registerWithProfile(account, profile);

      addToast({
        variant: 'info',
        title: 'Account created! Check your inbox for a verification link.',
      });
      navigate('/strippedTimeline');
    } catch (err) {
      console.error('Registration failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{titles.register}</title>
      </Helmet>
      <RegisterArea>
        <AuthSkyline />
        <Area
          css={{
            height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
            marginTop: `calc(${NAVIGATION_HEIGHT})`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <StepperArea>
            <ProgressStepper steps={stepLabels} currentStep={step} />
          </StepperArea>

          <FormArea>
            <Form onSubmit={(e) => e.preventDefault()}>
              {/* ── Header: always show logo, back button when step > 0 ── */}
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  minHeight: '48px',
                }}
              >
                {step > 0 && (
                  <div style={{ position: 'absolute', left: 0 }}>
                    <BackButton aria-label="Go back" onClick={prev}>
                      <IconButton type="button">
                        <ChevronLeft size={25} />
                      </IconButton>
                    </BackButton>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 'max-content',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '1.8rem',
                      lineHeight: '1',
                      color: 'var(--colors-slate12, #1e293b)',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    tute
                  </span>
                  <div
                    style={{
                      width: '100%',
                      height: '16px',
                      marginTop: '0.1rem',
                    }}
                  >
                    <BookLogo />
                  </div>
                </div>
              </div>

              <Tagline style={{ marginBottom: '0.5rem', textAlign: 'center' }}>
                {stepTaglines[step]}
              </Tagline>

              <StepContent>
                {step === 0 && (
                  <PersonalDetailsStep
                    data={account}
                    onChange={setAccount}
                    onValidityChange={setDetailsValid}
                  />
                )}
                {step === 1 && (
                  <ExpertiseStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setExpertiseValid}
                    role="parent"
                  />
                )}
                {step === 2 && (
                  <>
                    <AvailabilityTimezone
                      timezone={
                        profile.timezone ||
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      }
                      onTimezoneChange={(tz) => savePatch({ timezone: tz })}
                    />
                    <AvailabilityStep
                      data={profile}
                      onChange={savePatch}
                      onValidityChange={setAvailabilityValid}
                    />
                  </>
                )}
              </StepContent>

              <FormActions>
                <Button
                  type="button"
                  block
                  tone="success"
                  disabled={!canProceed() || isSubmitting}
                  onClick={handleNext}
                >
                  {isSubmitting
                    ? 'Creating account…'
                    : step < 2
                      ? 'Next'
                      : 'Finish'}
                </Button>
                <Link
                  to="/login"
                  style={{ marginTop: '1rem', display: 'inline-block' }}
                >
                  Already have an account? Sign in here
                </Link>
              </FormActions>
            </Form>
          </FormArea>
        </Area>
      </RegisterArea>
    </>
  );
};

export default ParentRegister;